import { createContext, useContext, useEffect, useRef, useState } from "react";
import { api, formatApiError, setTimezoneOverride } from "@/lib/api";
import { clearGridMemory } from "@/lib/gridMemory";
import { rememberAccount } from "@/lib/accounts";

const AuthContext = createContext(null);
export const SESSION_KEY = "hc_session";

const announce = (u) => { if (u) rememberAccount(u); try { localStorage.setItem(SESSION_KEY, JSON.stringify({ id: u ? u.id : null, at: Date.now() })); } catch { /* storage unavailable */ } };
const toast = (msg, opts) => import("sonner").then(({ toast: t }) => t(msg, opts));

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, obj=user
  const [error, setError] = useState("");
  const hadUser = useRef(false);
  const userRef = useRef(null);
  useEffect(() => { userRef.current = user; if (user) hadUser.current = true; }, [user]);

  useEffect(() => {
    let mounted = true;
    api.get("/auth/me").then((r) => mounted && setUser(r.data)).catch(() => mounted && setUser(false));
    const expired = () => {
      if (!hadUser.current) return;
      setUser(false);
      toast("Your session ended for security. Please sign in again.", { id: "session-expired" });
    };
    // Another tab signed in/out (cookies are shared across tabs): follow it instead of showing a broken page.
    const onStorage = (e) => {
      if (e.key !== SESSION_KEY || !e.newValue) return;
      const { id } = JSON.parse(e.newValue);
      const cur = userRef.current;
      if (!cur && !id) return;
      if (cur && id === cur.id) return;
      if (!id) {
        setUser(false);
        toast("You signed out in another tab, so this tab was signed out too.", { id: "session-other-tab" });
        return;
      }
      toast("You signed in as a different account in another tab — refreshing this one.", { id: "session-other-tab" });
      setTimeout(() => window.location.reload(), 900);
    };
    // Coming back to a tab: make sure it still belongs to the same account.
    const onVisible = async () => {
      if (document.visibilityState !== "visible" || !userRef.current) return;
      try {
        const r = await api.get("/auth/me");
        if (r.data.id !== userRef.current.id) window.location.reload();
      } catch { /* 401 path handled by the api interceptor → auth:expired */ }
    };
    window.addEventListener("auth:expired", expired);
    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      mounted = false;
      window.removeEventListener("auth:expired", expired);
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  const login = async (email, password, role) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", role ? { email, password, role } : { email, password });
      if (!data.mfa_required && !data.pick_role) { setUser(data); announce(data); }
      return data;
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  };

  const register = async (payload) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", payload);
      setUser(data);
      announce(data);
      return data;
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Logout request failed; clearing local session anyway", e?.message);
    }
    setUser(false);
    setTimezoneOverride(null);
    clearGridMemory();
    announce(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, error }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
